import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AccountsService } from '../accounts.service';
import { formatCountdown, PASSWORDLESS_GENERIC_MESSAGE, PasswordlessAuthService, startOtpCountdowns } from '../passwordless';
import { AuthShellComponent } from '../shell';
import { OtpFormInterface, OtpFormValuesInterface } from './otp.interface';

/**
 * Login with a 6-digit e-mail OTP after the address has been confirmed.
 */
@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent],
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpComponent extends BaseService implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly accountsService = inject(AccountsService);
  private readonly passwordlessAuthService = inject(PasswordlessAuthService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  formOtp: FormGroup;
  remainingSeconds = 0;
  validitySeconds = 0;
  hasRequestedCode = false;
  readonly formatCountdown = formatCountdown;
  private stopCooldown: (() => void) | null = null;

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.stopCooldown?.();
  }

  createForm(): void {
    this.formOtp = new FormGroup<OtpFormInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
      code: new FormControl('', [RxwebValidators.required(), RxwebValidators.minLength({ value: 6 }), RxwebValidators.maxLength({ value: 6 })]),
    });
  }

  async requestCode(): Promise<void> {
    if (this.remainingSeconds > 0) {
      return;
    }
    const emailControl = this.formOtp.get('email');
    emailControl?.markAsTouched();
    if (emailControl?.invalid) {
      return;
    }
    const formValues: OtpFormValuesInterface = this.formOtp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .requestEmailOtp(formValues.email)
      .then(() => {
        this.hasRequestedCode = true;
        this._toast.success({ message: PASSWORDLESS_GENERIC_MESSAGE });
        this.beginCooldown();
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
        this.changeDetectorRef.markForCheck();
      });
  }

  async verifyCode(): Promise<void> {
    const formValues: OtpFormValuesInterface = this.formOtp.getRawValue();
    this._spin.start();
    await this.passwordlessAuthService
      .verifyEmailOtp(formValues.email, formValues.code)
      .then(async (): Promise<void> => {
        await this.accountsService.loadAccountIds();
        this._toast.success({ message: 'Login realizado com sucesso!' });
        void this.router.navigateByUrl('/dashboard');
      })
      .catch((error: Error) => {
        this._toast.error({ message: error.message });
      })
      .finally(() => {
        this._spin.stop();
        this.changeDetectorRef.markForCheck();
      });
  }

  private beginCooldown(): void {
    this.stopCooldown?.();
    this.stopCooldown = startOtpCountdowns((state): void => {
      this.remainingSeconds = state.resendSeconds;
      this.validitySeconds = state.validitySeconds;
      this.changeDetectorRef.markForCheck();
    });
  }
}
